import {
  ADMIN_COUNTRY_SCOPE_STORAGE_KEY,
  ADMIN_STORAGE_KEY,
  ADMIN_TOKEN_STORAGE_KEY,
  currentAdminAuthToken,
  currentAdminCountryScope,
  currentSupportAuthToken,
  currentVendorAuthToken,
  SUPPORT_STORAGE_KEY,
  SUPPORT_TOKEN_STORAGE_KEY,
  VENDOR_STORAGE_KEY,
  VENDOR_TOKEN_STORAGE_KEY,
} from "helpers/storage";
import axios from "Lib/Axios/index";
import {
  adminRoutes,
  subdomainRoutes,
  supportRoutes,
  vendorRoutes,
} from "routes/routes";
import { handleError, removeFalsyValues } from "./misc";
import { resolveSubdomain } from "./subdomain";

const baseURL = process.env.REACT_APP_BASE_URL;

const withAdminCountryScope = (url: string) => {
  if (!url.startsWith("admin/")) {
    return url;
  }

  const country = currentAdminCountryScope();
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}country=${encodeURIComponent(country || "all")}`;
};

const userToken = (url?: string) => {
  if (url?.startsWith("admin/")) {
    return currentAdminAuthToken();
  }
  if (url?.startsWith("support/")) {
    return currentSupportAuthToken();
  }
  if (url?.startsWith("vendor/")) {
    return currentVendorAuthToken();
  }

  const subdomain = resolveSubdomain(window.location).split(".")[0];

  switch (subdomain) {
    case subdomainRoutes.admin:
      return currentAdminAuthToken();
    case subdomainRoutes.support:
      return currentSupportAuthToken();
    default:
      return currentVendorAuthToken();
  }
};

export const logout = () => {
  localStorage.removeItem(VENDOR_STORAGE_KEY);
  localStorage.removeItem(VENDOR_TOKEN_STORAGE_KEY);
  window.location.replace(vendorRoutes.login);
};

export const logoutAdmin = () => {
  localStorage.removeItem(ADMIN_STORAGE_KEY);
  localStorage.removeItem(ADMIN_TOKEN_STORAGE_KEY);
  localStorage.removeItem(ADMIN_COUNTRY_SCOPE_STORAGE_KEY);
  window.location.replace(adminRoutes.login);
};

export const logoutSupport = () => {
  localStorage.removeItem(SUPPORT_STORAGE_KEY);
  localStorage.removeItem(SUPPORT_TOKEN_STORAGE_KEY);
  window.location.replace(supportRoutes.login);
};

const logoutForUrl = (url: string) => {
  if (url.startsWith("admin/")) {
    logoutAdmin();
    return;
  }
  if (url.startsWith("support/")) {
    logoutSupport();
    return;
  }
  const subdomain = resolveSubdomain(window.location).split(".")[0];
  if (subdomain === subdomainRoutes.admin) return logoutAdmin();
  if (subdomain === subdomainRoutes.support) return logoutSupport();
  logout();
};

const handleRequestError = (error: any, url: string) => {
  if (error?.code === "ERR_CANCELED" || !error?.response) throw error;
  const response = error.response;
  if (response?.status === 401) {
    logoutForUrl(url);
  }

  handleError(response);
  throw response?.data || error;
};

export const post = (url: string, data: any) => {
  const token = userToken(url);

  return axios
    .post(`${baseURL}${withAdminCountryScope(url)}`, data, {
      headers: {
        ...removeFalsyValues({
          Authorization: token ? `Bearer ${token}` : undefined,
        }),
      },
    })
    .then(({ data }) => {
      return data;
    })
    .catch((error) => handleRequestError(error, url));
};

export const get = (
  url: string,
  signal?: AbortSignal,
  responseType: "json" | "text" = "json",
) => {
  const token = userToken(url);

  return axios
    .get(`${baseURL}${withAdminCountryScope(url)}`, {
      ...(signal ? { signal } : {}),
      ...(responseType === "text" ? { responseType } : {}),
      headers: {
        ...removeFalsyValues({
          Authorization: token ? `Bearer ${token}` : undefined,
        }),
      },
    })

    .then(({ data }) => (responseType === "text" ? data : data?.data))

    .catch((error) => {
      // CSV endpoints still return JSON error envelopes (including 401/422).
      if (
        responseType === "text" &&
        typeof error?.response?.data === "string"
      ) {
        try {
          error.response.data = JSON.parse(error.response.data);
        } catch {
          /* Non-JSON server error. */
        }
      }
      return handleRequestError(error, url);
    });
};

export const put = (url: string, data: any) => {
  const token = userToken(url);

  return axios
    .put(`${baseURL}${withAdminCountryScope(url)}`, data, {
      headers: {
        ...removeFalsyValues({
          Authorization: token ? `Bearer ${token}` : undefined,
        }),
      },
    })
    .then(({ data }) => data)
    .catch((error) => handleRequestError(error, url));
};

export const deleteRequest = (url: string, data?: any) => {
  const token = userToken(url);

  return axios
    .delete(`${baseURL}${withAdminCountryScope(url)}`, {
      data,
      headers: {
        ...removeFalsyValues({
          Authorization: token ? `Bearer ${token}` : undefined,
        }),
      },
    })
    .then(({ data }) => data)
    .catch((error) => handleRequestError(error, url));
};

export const patch = (url: string, data: any) => {
  const token = userToken(url);

  return axios
    .patch(`${baseURL}${withAdminCountryScope(url)}`, data, {
      headers: {
        ...removeFalsyValues({
          Authorization: token ? `Bearer ${token}` : undefined,
        }),
      },
    })
    .catch((error) => handleRequestError(error, url));
};

/** Authenticated text exports use the same session and market scope as JSON reads. */
export const getText = (url: string): Promise<string> => {
  const token = userToken(url);
  return axios
    .get(`${baseURL}${withAdminCountryScope(url)}`, {
      responseType: "text",
      headers: {
        ...removeFalsyValues({
          Authorization: token ? `Bearer ${token}` : undefined,
        }),
      },
    })
    .then(({ data }) => String(data))
    .catch((error) => handleRequestError(error, url));
};
